/**
 * Homepage State Hook - Composition hook
 *
 * Combines useHomepageData with data transformations.
 * This is the main hook consumed by HomepageProvider.
 */
import { useHomepageData } from './useHomepageData'
import { useHomepageReviews } from './useHomepageReviews'

import type { HomepageBanner, HomepageBrandCard, HomepageCategoryCard, HomepageProductSlider, HomepagePromoBlock, ReviewCard } from '../../types'

export interface UseHomepageStateResult {
  /** Raw config data */
  config: {
    banners: HomepageBanner[]
    product_sliders: HomepageProductSlider[]
    promo_blocks: HomepagePromoBlock[]
    category_cards: HomepageCategoryCard[]
    brand_cards: HomepageBrandCard[]
  } | null
  /** Filtered banners (null items removed) */
  banners: HomepageBanner[]
  /** Filtered product sliders (null items removed) */
  productSliders: HomepageProductSlider[]
  /** Filtered promo blocks (null items removed) */
  promoBlocks: HomepagePromoBlock[]
  /** Filtered category cards (null items removed) */
  categoryCards: HomepageCategoryCard[]
  /** Filtered brand cards (null items removed) */
  brandCards: HomepageBrandCard[]
  /** Loading state */
  isLoading: boolean
  /** Normalized error (Error | null) */
  error: Error | null
  // --- Reviews (separate query) ---
  /** Transformed review cards */
  reviews: ReviewCard[]
  /** Loading state for reviews query */
  reviewsLoading: boolean
}

/** Lists come from GraphQL with possible null entries. */
const withoutNulls = <T>(items: T[] | null | undefined): NonNullable<T>[] =>
  (items ?? []).filter((item): item is NonNullable<T> => item !== null)

const withDisplayType = (slider: HomepageProductSlider): HomepageProductSlider => ({
  ...slider,
  display_type: slider.display_type ?? 'slider',
})

/** React Query types the error as Error, a rejected promise can still carry any value. */
const toError = (error: Error | null): Error | null => {
  if (error instanceof Error) {
    return error
  }

  return error ? new Error(String(error)) : null
}

/**
 * Composition hook for homepage state
 *
 * Combines data fetching with filtering and error normalization.
 * Also composes the reviews query (separate cache).
 */
export const useHomepageState = (): UseHomepageStateResult => {
  const query = useHomepageData()
  const reviewsQuery = useHomepageReviews()

  const config = query.data?.homepageConfig ?? null

  return {
    config,
    banners: withoutNulls(config?.banners),
    productSliders: withoutNulls(config?.product_sliders).map(withDisplayType),
    promoBlocks: withoutNulls(config?.promo_blocks),
    categoryCards: withoutNulls(config?.category_cards),
    brandCards: withoutNulls(config?.brand_cards),
    isLoading: query.isLoading,
    error: toError(query.error),
    // Reviews (separate query)
    reviews: reviewsQuery.reviews,
    reviewsLoading: reviewsQuery.isLoading,
  }
}
